import React, { useEffect, useMemo, useState, useRef } from 'react';
import '../../../../assets/css/modal.css';
import { useFontTemplateStore } from '../../../../store/useFontTemplateStore';
import {
    CssFontCreate,
    FontTemplateDetailResponse,
    FontUpdateOrCreate,
    FontFile,
    FontUpdate
} from "../../../../type/cssTemplate";
import { toast } from "react-toastify";
import { useLoginStore } from "../../../../store/useLogin";
import { convertToS3Url, createFontFaceRule, convertCreateFontToDetail } from '../../../../util/fontUtils';
import FontListPanel from '../../../modal/font/FontListPanel';
import apiClient from '../../../../service/apiClient';

interface ITemplateFontModal {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    productId?: string;
}

/**
 * 기준의 안정 식별자.
 * - 기존 기준: 백엔드에서 받은 id
 * - 신규 기준(저장 전): handleAddStandard에서 발급한 localKey
 * cssName(편집 가능한 CSS selector 문자열)과 분리해서 React key/선택 추적용으로 사용.
 */
const getStableKey = (standard: { id?: string; localKey?: string }): string | undefined =>
    standard.id || standard.localKey;

/** 저장 전(서버 ID 없음) 신규 기준인지 판별 */
const isNewStandard = (standard: { id?: string }): boolean => !standard.id;

const TemplateFontModal: React.FC<ITemplateFontModal> = ({
    isOpen,
    onClose,
    title = '템플릿 폰트 설정',
    productId = ''
}) => {
    const { role } = useLoginStore();
    const { getCssTemplate, saveBasicCssTemplate, saveCssTemplate } = useFontTemplateStore();

    // State
    const [loading, setLoading] = useState(false);
    const [template, setTemplate] = useState<FontTemplateDetailResponse | null>(null);
    const [createList, setCreateList] = useState<CssFontCreate[]>([]);
    const [updateList, setUpdateList] = useState<Map<string, FontUpdateOrCreate>>(new Map());
    const [saveList, setSaveList] = useState<FontUpdate[]>([]);
    const [selectedFonts] = useState<Set<string>>(new Set());
    /** 선택된 기준의 안정 식별자 (getStableKey 결과). cssName 편집 중에도 안 변함. */
    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);

    const fontStyleRef = useRef<HTMLStyleElement | null>(null);
    const [, setLoadedFonts] = useState<Set<string>>(new Set());
    const [refreshVersion, setRefreshVersion] = useState<Map<string, number>>(new Map());
    const scrollPositionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
    const [newRemark, setNewRemark] = useState('');

    // 폰트 동적 로드 함수
    const loadFont = (fontUrl: string, fontName: string) => {
        if (!fontStyleRef.current) {
            fontStyleRef.current = document.createElement('style');
            document.head.appendChild(fontStyleRef.current);
        }
        const fontFace = createFontFaceRule(fontUrl, fontName);
        fontStyleRef.current.textContent += fontFace;
        setLoadedFonts(prev => new Set(prev).add(fontName));
    };

    // 데이터 로드 시 모든 폰트 로드
    useEffect(() => {
        if (template?.fonts) {
            template.fonts.forEach(standard => {
                standard.originFile?.forEach((fontFile, index) => {
                    if (fontFile.fullUrl) {
                        loadFont(fontFile.fullUrl, `${standard.cssName}_origin_${index}`);
                    }
                    if (fontFile.boldFontFile?.fullUrl) {
                        loadFont(fontFile.boldFontFile.fullUrl, `${standard.cssName}_origin_${index}_bold`);
                    }
                });
            });
        }
    }, [template]);

    // Data Fetching
    useEffect(() => {
        if (isOpen && productId) {
            setLoading(true);
            getCssTemplate(productId).then(data => {
                // Process bold font info from backend to create nested boldFontFile object
                if (data && data.fonts) {
                    const transformFontFile = (ff: any) => {
                        if (ff && ff.boldFontId) {
                            const newFf = { ...ff };
                            newFf.boldFontFile = {
                                id: ff.boldFontId,
                                originalFileName: ff.boldFontName,
                                fileFullUrl: ff.boldFontUrl,
                                fullUrl: convertToS3Url(ff.boldFontUrl),
                                fileSize: 0,
                                use: false,
                            };
                            delete newFf.boldFontId;
                            delete newFf.boldFontName;
                            delete newFf.boldFontUrl;
                            return newFf;
                        }
                        return ff;
                    };

                    data.fonts.forEach(font => {
                        if (font.file) {
                            font.file = transformFontFile(font.file);
                        }
                        if (font.originFile) {
                            font.originFile = font.originFile.map(transformFontFile);
                        }
                    });
                }
                setTemplate(data);
                if (data && data.fonts && data.fonts.length > 0) {
                    // 기존 기준은 id를 stable key로 사용
                    setSelectedKey(data.fonts[0].id || null);
                }
                setLoading(false);
            });
        } else if (!isOpen) {
            // Reset states
            setTemplate(null);
            setCreateList([]);
            setUpdateList(new Map());
            setSelectedKey(null);
        }
    }, [isOpen, productId, getCssTemplate]);

    // Memoized derived data
    const allStandards = useMemo(() => {
        const existing = template?.fonts || [];
        const newlyCreated = createList.map(c => convertCreateFontToDetail(c));
        return [...existing, ...newlyCreated];
    }, [template, createList]);

    const selectedFontDetail = useMemo(() => {
        if (selectedKey == null) return null;
        return allStandards.find(s => getStableKey(s) === selectedKey) || null;
    }, [selectedKey, allStandards]);

    const hasPendingChanges = useMemo(() => {
        if (role === 'ROLE_ADMIN') {
            return createList.length > 0 || updateList.size > 0;
        }
        return saveList.length > 0;
    }, [role, createList, updateList, saveList]);
    const isSaveDisabled = isUploading || !hasPendingChanges;

    // Handlers
    const handleAddStandard = () => {
        if (!newRemark.trim()) {
            toast.warn('추가할 기준의 이름을 입력하세요.');
            return;
        }
        const trimmedRemark = newRemark.trim();

        if (allStandards.some(s => s.remark === trimmedRemark)) {
            toast.error('이미 사용중인 이름입니다.');
            return;
        }

        const timestamp = Date.now();
        const localKey = `local_${timestamp}`;
        const newCssName = `temp_${timestamp}`;
        const newFont: CssFontCreate = {
            id: '',
            localKey,
            cssName: newCssName,
            categoryName: '',
            remark: trimmedRemark,
            file: { id: '0', fileName: `temp_new_${timestamp}`, fileFullUrl: '', fileUrl: '', fileSize: 0 },
            originFile: []
        };
        setCreateList(prev => [...prev, newFont]);
        // 신규 기준의 stable key는 localKey
        setSelectedKey(localKey);
        setNewRemark('');
    };

    const handleSave = async () => {
        if (role === 'ROLE_ADMIN') {
            // 신규 기준은 폰트 파일 1개 이상 필수. 빈 기준 저장 시 백엔드가 아무것도 INSERT 안 함.
            const emptyNewStandard = createList.find(
                item => !item.originFile || item.originFile.length === 0
            );
            if (emptyNewStandard) {
                toast.error(
                    `"${emptyNewStandard.remark || '신규 기준'}"에 폰트 파일을 1개 이상 업로드해주세요.`
                );
                return;
            }
            // 신규 기준의 cssName이 초기 temp_ 값 그대로거나 비어있으면 사용자가 변경 기준을 안 정한 것.
            const invalidCssName = createList.find(
                item => !item.cssName || !item.cssName.trim() || item.cssName.startsWith('temp_')
            );
            if (invalidCssName) {
                toast.error(
                    `"${invalidCssName.remark || '신규 기준'}"의 변경 기준(CSS selector)을 입력해주세요.`
                );
                return;
            }
            // 기존 기준의 cssName이 비어있게 편집된 경우도 차단
            const invalidUpdate = Array.from(updateList.values()).find(
                item => !item.cssName || !item.cssName.trim()
            );
            if (invalidUpdate) {
                toast.error(
                    `"${invalidUpdate.remark || '수정한 기준'}"의 변경 기준(CSS selector)을 입력해주세요.`
                );
                return;
            }

            const createData: FontUpdateOrCreate[] = createList.map(item => ({
                cssName: item.cssName,
                categoryName: '',
                remark: item.remark,
                fonts: (item.originFile || []).map(file => ({
                    fileId: file.id,
                    mainYn: file.use,
                    boldFontFileId: file.boldFontFile?.id
                }))
            }));
            const updateData: FontUpdateOrCreate[] = Array.from(updateList.values());
            const combinedData = [...createData, ...updateData];

            if (combinedData.length === 0) {
                toast.info('변경 사항이 없습니다.');
                return;
            }
            try {
                await saveBasicCssTemplate(combinedData, productId);
                toast.success('저장되었습니다.');
                onClose();
            } catch (error) {
                toast.error('저장에 실패했습니다.');
            }
        } else { // ROLE_USER
            if (saveList.length === 0) {
                toast.info('변경 사항이 없습니다.');
                return;
            }
            try {
                await saveCssTemplate(saveList, productId);
                toast.success('저장되었습니다.');
                onClose();
            } catch (error) {
                toast.error('저장에 실패했습니다.');
            }
        }
    };

    const handleAddFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0 || !selectedFontDetail) return;

        setIsUploading(true);
        const { cssName } = selectedFontDetail;
        const isNew = isNewStandard(selectedFontDetail);

        try {
            const uploadedFilesInfo = await Promise.all(files.map(async (file) => {
                const formData = new FormData();
                formData.append('file', file);
                const response = await apiClient.post('/upload-file/upload', formData);
                return { uploadedData: response.data, originalFile: file };
            }));

            const newFontFiles: FontFile[] = uploadedFilesInfo.map(({ uploadedData, originalFile }) => ({
                id: uploadedData.id,
                originalFileName: uploadedData.originalFileName || originalFile.name,
                fileFullUrl: uploadedData.fileFullUrl,
                fileUrl: uploadedData.fileUrl,
                fullUrl: convertToS3Url(uploadedData.fileFullUrl),
                fileSize: uploadedData.fileSize || originalFile.size,
                use: false
            }));

            if (isNew) {
                // localKey로 찾기 (cssName 편집 영향 없음)
                setCreateList(prev => prev.map(std => {
                    if (std.localKey === selectedFontDetail.localKey) {
                        const updatedOriginFile = [...(std.originFile || []), ...newFontFiles];
                        if ((std.originFile || []).length === 0 && updatedOriginFile.length > 0) {
                            updatedOriginFile[0].use = true;
                        }
                        return { ...std, originFile: updatedOriginFile };
                    }
                    return std;
                }));
            } else { // Existing standard — id로 찾기
                setTemplate(prev => {
                    if (!prev || !prev.fonts) return prev;
                    const newFonts = prev.fonts.map(font => {
                        if (font.id === selectedFontDetail.id) {
                            return { ...font, originFile: [...(font.originFile || []), ...newFontFiles] };
                        }
                        return font;
                    });
                    return { ...prev, fonts: newFonts };
                });

                setUpdateList(prev => {
                    const newMap = new Map(prev);
                    const fontToUpdate = allStandards.find(f => f.id === selectedFontDetail.id);
                    if (fontToUpdate) {
                        const existing = newMap.get(cssName) || {
                            cssName: cssName,
                            categoryName: fontToUpdate.categoryName,
                            remark: fontToUpdate.remark,
                            fonts: (fontToUpdate.originFile || []).map(f => ({
                                cssFontId: f.cssFontId, fileId: f.id, mainYn: f.use, boldFontFileId: f.boldFontFile?.id
                            }))
                        };
                        const newFilesForUpdate = newFontFiles.map(f => ({ fileId: f.id, mainYn: false, boldFontFileId: undefined }));
                        if(!existing.fonts) existing.fonts = [];
                        existing.fonts.push(...newFilesForUpdate);
                        newMap.set(cssName, existing);
                    }
                    return newMap;
                });
            }

            newFontFiles.forEach(f => {
                if (f.fullUrl) {
                    const allOriginFiles = [...(selectedFontDetail.originFile || []), ...newFontFiles];
                    const newIndex = allOriginFiles.findIndex(origin => origin.id === f.id);
                    loadFont(f.fullUrl, `${cssName}_origin_${newIndex}`);
                }
            });
            // refreshVersion은 stable key 기준
            const stable = getStableKey(selectedFontDetail) ?? cssName;
            setRefreshVersion(prev => new Map(prev).set(stable, (prev.get(stable) || 0) + 1));

            toast.success(`${files.length}개의 파일이 추가되었습니다.`);

        } catch (error) {
            console.error('파일 업로드 실패:', error);
            toast.error('파일 업로드에 실패했습니다.');
        } finally {
            setIsUploading(false);
            e.target.value = '';
        }
    };
    const onNavigateFont = (direction: 'prev' | 'next') => { /* ... */ };
    const onFontUseChange = (fontFile: FontFile, index: number) => {
        if (!selectedFontDetail) return;

        const { cssName } = selectedFontDetail;
        const isNew = isNewStandard(selectedFontDetail);

        // 1. template / createList state 업데이트 (화면 표시용) — stable key 기준
        if (isNew) {
            setCreateList(prev => prev.map(item => {
                if (item.localKey !== selectedFontDetail.localKey) return item;
                const updatedOriginFile = (item.originFile || []).map((origin, idx) => ({
                    ...origin,
                    use: idx === index
                }));
                return { ...item, originFile: updatedOriginFile };
            }));
        } else {
            setTemplate(prev => {
                if (!prev || !prev.fonts) return null;
                const newFonts = prev.fonts.map(font => {
                    if (font.id === selectedFontDetail.id) {
                        const updatedOriginFiles = (font.originFile || []).map((origin, idx) => ({
                            ...origin,
                            use: idx === index
                        }));
                        return { ...font, originFile: updatedOriginFiles, file: updatedOriginFiles[index] };
                    }
                    return font;
                });
                return { ...prev, fonts: newFonts };
            });
        }

        // 2. updateList/saveList에 변경사항 저장 (서버 전송용)
        if (role === 'ROLE_ADMIN' && !isNew) {
            setUpdateList(prev => {
                const newMap = new Map(prev);
                const fontToUpdate = allStandards.find(f => f.id === selectedFontDetail.id);
                if (fontToUpdate) {
                    const updatedFonts = (fontToUpdate.originFile || []).map((origin, idx) => ({
                        cssFontId: origin.cssFontId,
                        fileId: origin.id,
                        mainYn: idx === index,
                        boldFontFileId: origin.boldFontFile?.id
                    }));

                    newMap.set(cssName, {
                        cssName: cssName,
                        categoryName: fontToUpdate.categoryName,
                        remark: fontToUpdate.remark,
                        fonts: updatedFonts
                    });
                }
                return newMap;
            });
        } else if (role !== 'ROLE_ADMIN') {
            const selectedOriginFile = selectedFontDetail.originFile?.[index];
            if (selectedOriginFile) {
                 const updatedFont: FontUpdate = {
                    cssName: cssName,
                    cssFontId: selectedOriginFile.cssFontId || selectedOriginFile.id
                };
                setSaveList(prev => {
                    const filtered = prev.filter(item => item.cssName !== cssName);
                    return [...filtered, updatedFont];
                });
            }
        }

        // 3. 폰트 버전 업데이트 (미리보기 갱신용) — stable key 기준
        const stable = getStableKey(selectedFontDetail) ?? cssName;
        setRefreshVersion(prev => new Map(prev).set(stable, (prev.get(stable) || 0) + 1));
    };
    const onBoldUpload = async (e: React.ChangeEvent<HTMLInputElement>, regularFontFile: FontFile) => {
        const file = e.target.files?.[0];
        if (!file || !selectedFontDetail) return;

        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await apiClient.post('/upload-file/upload', formData);
            const uploadedFileData = response.data;

            const newBoldFontFile: FontFile = {
                id: uploadedFileData.id,
                originalFileName: uploadedFileData.originalFileName || file.name,
                fileFullUrl: uploadedFileData.fileFullUrl,
                fileUrl: uploadedFileData.fileUrl,
                fullUrl: convertToS3Url(uploadedFileData.fileFullUrl),
                fileSize: uploadedFileData.fileSize || file.size,
                use: false,
            };

            const { cssName } = selectedFontDetail;
            const isNew = isNewStandard(selectedFontDetail);

            const updateOriginFile = (originFile: FontFile[] | undefined) => (originFile || []).map(origin =>
                origin.id === regularFontFile.id ? { ...origin, boldFontFile: newBoldFontFile } : origin
            );

            if (isNew) {
                setCreateList(prev => prev.map(std =>
                    std.localKey === selectedFontDetail.localKey
                        ? { ...std, originFile: updateOriginFile(std.originFile) }
                        : std
                ));
            } else {
                setTemplate(prev => {
                    if (!prev || !prev.fonts) return prev;
                    const newFonts = prev.fonts.map(font =>
                        font.id === selectedFontDetail.id
                            ? { ...font, originFile: updateOriginFile(font.originFile) }
                            : font
                    );
                    return { ...prev, fonts: newFonts };
                });

                setUpdateList(prev => {
                    const newMap = new Map(prev);
                    const fontToUpdate = allStandards.find(f => f.id === selectedFontDetail.id);
                    if (fontToUpdate) {
                         const existing = newMap.get(cssName) || {
                            cssName: cssName,
                            categoryName: fontToUpdate.categoryName,
                            remark: fontToUpdate.remark,
                            fonts: (fontToUpdate.originFile || []).map(f => ({
                                cssFontId: f.cssFontId, fileId: f.id, mainYn: f.use, boldFontFileId: f.boldFontFile?.id
                            }))
                        };
                        const updatedFonts = existing.fonts.map(font => {
                            if (font.fileId === regularFontFile.id || font.cssFontId === regularFontFile.id) {
                                return { ...font, boldFontFileId: newBoldFontFile.id };
                            }
                            return font;
                        });
                        newMap.set(cssName, { ...existing, fonts: updatedFonts });
                    }
                    return newMap;
                });
            }
            
            const regularFontIndex = selectedFontDetail.originFile?.findIndex(f => f.id === regularFontFile.id) ?? -1;
            if (regularFontIndex !== -1) {
                loadFont(newBoldFontFile.fullUrl, `${cssName}_origin_${regularFontIndex}_bold`);
            }
            const stable = getStableKey(selectedFontDetail) ?? cssName;
            setRefreshVersion(prev => new Map(prev).set(stable, (prev.get(stable) || 0) + 1));

            toast.success(`굵은 폰트가 추가되었습니다.`);

        } catch (error) {
            console.error('굵은 폰트 업로드 실패:', error);
            toast.error('굵은 폰트 업로드에 실패했습니다.');
        } finally {
            setIsUploading(false);
            e.target.value = '';
        }
    };

    const handleCssNameChange = (newCssName: string) => {
        if (selectedKey == null || !selectedFontDetail) return;

        // 중복 검사 — 자기 자신이 아닌 다른 기준에 같은 cssName이 있을 때
        if (
            newCssName.trim() !== '' &&
            allStandards.some(
                s => s.cssName === newCssName && getStableKey(s) !== selectedKey
            )
        ) {
            toast.error('이미 사용중인 변경 기준 이름입니다.');
            // 강제 리렌더로 input 값을 원래대로 되돌림
            setRefreshVersion(prev => new Map(prev).set(selectedKey, (prev.get(selectedKey) || 0) + 1));
            return;
        }

        const oldCssName = selectedFontDetail.cssName;
        const isNew = isNewStandard(selectedFontDetail);

        if (isNew) {
            // createList의 cssName만 업데이트, selectedKey(localKey)는 그대로
            setCreateList(prev =>
                prev.map(item =>
                    item.localKey === selectedKey ? { ...item, cssName: newCssName } : item
                )
            );
        } else {
            // 기존 기준의 cssName 변경
            setTemplate(prev => {
                if (!prev || !prev.fonts) return prev;
                return {
                    ...prev,
                    fonts: prev.fonts.map(font =>
                        font.id === selectedKey ? { ...font, cssName: newCssName } : font
                    )
                };
            });

            // updateList Map은 cssName-keyed (백엔드 통신용) → 키 재발급
            setUpdateList(prev => {
                const newMap = new Map(prev);
                const existingData = newMap.get(oldCssName);
                if (existingData) {
                    newMap.delete(oldCssName);
                    newMap.set(newCssName, { ...existingData, cssName: newCssName });
                } else {
                    const fontToUpdate = allStandards.find(f => f.id === selectedKey);
                    if (fontToUpdate) {
                        newMap.set(newCssName, {
                            cssName: newCssName,
                            categoryName: fontToUpdate.categoryName,
                            remark: fontToUpdate.remark,
                            fonts: (fontToUpdate.originFile || []).map(f => ({
                                cssFontId: f.cssFontId,
                                fileId: f.id,
                                mainYn: f.use,
                                boldFontFileId: f.boldFontFile?.id
                            }))
                        });
                    }
                }
                return newMap;
            });
        }
        // selectedKey는 변경하지 않음 — stable id/localKey 유지
    };

    if (!isOpen) return null;

    return (
        <div className="template-modal-overlay" onClick={onClose}>
            <div className="template-modal-container max-w-6xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="template-modal-header">
                    <div><h2>{title}</h2></div>
                    <button className="template-modal-close" onClick={onClose}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                    </button>
                </div>

                <div className="flex flex-col md:flex-row gap-4 p-3.5 flex-1 overflow-y-auto">
                    {/* 변경 위치 목록 */}
                    <div className="w-full md:w-1/5 overflow-y-auto pr-4 md:border-r border-gray-200">
                        <h3 className="text-base font-semibold text-gray-700 mb-3">변경위치</h3>
                        {loading && <div className="text-xs text-gray-500">불러오는 중...</div>}
                        <div className="grid grid-cols-1 gap-2">
                            {allStandards.map((standard) => {
                                const stableKey = getStableKey(standard);
                                return (
                                    <button
                                        key={stableKey || standard.cssName}
                                        onClick={() => stableKey && setSelectedKey(stableKey)}
                                        className={`w-full text-left px-3 py-2.5 rounded-lg border transition-colors text-sm font-semibold ${selectedKey === stableKey ? 'bg-blue-100 border-blue-200 text-blue-800' : 'bg-white border-gray-200 text-gray-900 hover:bg-gray-50'}`}
                                    >
                                        {standard.remark || '이름 없음'}
                                    </button>
                                );
                            })}
                            {role === 'ROLE_ADMIN' && (
                                <div className="mt-3 pt-3 border-t border-gray-200">
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={newRemark}
                                            onChange={(e) => setNewRemark(e.target.value)}
                                            placeholder="신규 기준 이름"
                                            className="flex-grow w-0 px-2.5 py-1.5 text-xs border border-gray-300 rounded-md"
                                        />
                                        <button onClick={handleAddStandard} className="px-2.5 py-1.5 bg-gray-800 text-white text-xs rounded-md border border-gray-800 hover:bg-gray-700 flex-shrink-0">
                                            + 추가
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 폰트목록/미리보기 */}
                    <div className="flex-1 flex">
                        {selectedFontDetail ? (
                            <FontListPanel
                                // key는 stable 식별자만 사용. cssName 편집 시 unmount/remount 방지.
                                key={`${getStableKey(selectedFontDetail) ?? 'unknown'}_${refreshVersion.get(selectedKey || '') || 0}`}
                                selectedFontDetail={selectedFontDetail}
                                role={role}
                                isUploading={isUploading}
                                onAddFiles={handleAddFiles} // This needs full implementation
                                selectedFontsSize={selectedFonts.size}
                                currentFontIndex={0} // Placeholder
                                onNavigateFont={onNavigateFont}
                                onFontUseChange={onFontUseChange}
                                onBoldUpload={onBoldUpload}
                                onCssNameChange={handleCssNameChange}
                                productId={productId}
                                scrollPositionRef={scrollPositionRef}
                            />
                        ) : null}
                    </div>
                </div>

                <div className="template-modal-footer p-3 border-t border-gray-200">
                    <div className="flex items-center justify-end gap-2 w-full">
                        <button className="template-btn-cancel" onClick={onClose}>닫기</button>
                        <div className="relative ml-2">
                            {hasPendingChanges && (
                                <div className="pointer-events-none absolute bottom-[125%] left-1/2 z-50 w-max -translate-x-1/2 rounded-lg bg-[#d32f2f] px-4 py-[10px] text-[15px] font-bold text-white shadow-[0_4px_12px_rgba(0,0,0,0.25)]">
                                    저장해야 변경사항이 반영됩니다!
                                    <div className="absolute -bottom-2 left-1/2 h-0 w-0 -translate-x-1/2 border-x-[8px] border-x-transparent border-t-[8px] border-t-[#d32f2f]" />
                                </div>
                            )}
                            <button
                                onClick={handleSave}
                                className="template-btn-save rounded-lg bg-blue-600 px-6 py-[10px] text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
                                disabled={isSaveDisabled}
                            >
                                저장하기
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TemplateFontModal;
